import { useRef, type HTMLAttributes, type ReactNode } from "react";

import {
  useEscapeDismiss,
  useFocusRestore,
  useFocusTrap,
  useScrollLock,
} from "./layerUtils";

type ModalSize = "sm" | "md";

function classes(base: string[], className: string | undefined): string {
  if (className) {
    base.push(className);
  }

  return base.join(" ");
}

export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  /** Accessible name for the dialog (rendered as `aria-label`). */
  ariaLabel: string;
  children: ReactNode;
  isOpen: boolean;
  onDismiss: () => void;
  size?: ModalSize;
}

/**
 * Blocking overlay dialog — UI-LAW §4a Modal layer.
 *
 * Use ONLY for (a) destructive confirms that need the §2.12 name-match
 * pattern, and (b) one short, focused, blocking task whose result must not
 * be lost (e.g. a one-time API-key secret reveal). Anything heavier belongs
 * in a Drawer or Route; Modal-as-application is the failure mode the layer
 * taxonomy exists to prevent.
 *
 * Exactly one primary action inside the Modal is LAW for callers — the
 * component does not enforce it. The body scrolls internally and is capped
 * at 60vh (the §4a hard limit). Focus is trapped in the panel while open
 * and restored to the opener on close; Escape and the scrim dismiss.
 */
export function Modal({
  ariaLabel,
  children,
  className,
  isOpen,
  onDismiss,
  size = "sm",
  ...props
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Order matters: useFocusRestore must capture the opener BEFORE
  // useFocusTrap moves focus into the panel (effects run in call order).
  useFocusRestore(isOpen);
  useFocusTrap(panelRef, isOpen);
  useScrollLock(isOpen);
  useEscapeDismiss(onDismiss, isOpen);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="marker-modal" role="presentation">
      <button
        aria-label="Close dialog"
        className="marker-modal__scrim"
        onClick={onDismiss}
        type="button"
      />
      <div
        {...props}
        aria-label={ariaLabel}
        aria-modal="true"
        className={classes(
          ["marker-modal__panel", `marker-modal__panel--${size}`],
          className,
        )}
        ref={panelRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="marker-modal__body">{children}</div>
      </div>
    </div>
  );
}
